import { ChangeEvent } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ElementFormField, ElementTemplateFormField } from 'bom/components';
import { IElementTemplate } from 'bom/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { Size } from 'theme/enums';

import { IUpsertElementFormData } from './types';

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IUpsertElementFormData>) => void;
  onSubmit: (reset: UseFormReset<IUpsertElementFormData>) => (data: IUpsertElementFormData) => void;
  isEdit: boolean;
}

export const UpsertElementFormFields = ({ isOpen, onClose, onSubmit, isEdit }: IProps) => {
  const { t } = useTranslation();

  const { setValue, watch } = useFormContext();

  const useTemplateValue = watch('useElementTemplate');
  const elementNameValue = watch('elementName');
  const elementValue = watch('element');

  return (
    <Form
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('titleUpdateElement') : t('titleAddElement')}
      submitBtnLabel={t('buttonLabelSave')}
      onSubmit={onSubmit}
      level={0}
    >
      {!isEdit && (
        <>
          <FormField
            name="useElementTemplate"
            type={InputType.SWITCH}
            inputProps={{ label: t('labelUseElementTemplate'), size: Size.LG }}
          />

          <ElementTemplateFormField
            name="elementTemplate"
            onChange={(elementTemplate: IElementTemplate) => {
              if (!elementNameValue) {
                setValue('elementName', elementTemplate?.description);
              }

              if (!elementValue?.name) {
                setValue('element', { name: elementTemplate?.description });
              }
            }}
            required={useTemplateValue}
            disabled={!useTemplateValue}
          />

          {useTemplateValue && (
            <FormField
              name="elementName"
              type={InputType.TEXT_INPUT}
              inputProps={{
                label: t('labelElementName'),
                placeholder: t('placeholderEnterElementName'),
                onChange: (event: ChangeEvent<HTMLInputElement>) => {
                  setValue('element', { name: event.target.value });
                },
                required: true,
              }}
            />
          )}
        </>
      )}

      {!useTemplateValue && (
        <ElementFormField
          name="element"
          label={t('labelElementName')}
          placeholder={t('placeholderSelectElementName')}
          required
        />
      )}

      <FormField
        name="quantity"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: t('labelQuantity'),
          placeholder: t('placeholderElementQuantity'),
          slotProps: {
            input: {
              min: 0,
            },
          },
          required: true,
        }}
      />
    </Form>
  );
};
